/**
 * Error types returned by the GK-API and sent to the iframe as query params.
 */
export enum ErrorType {
  TokenRejected, // Default error
  NotUnique, // User failed the uniqueness check
  RevocationTooRecent, // User revoked their pass too recently
  // Location-related errors start
  BannedCountryDetected,
  BlockedCountryDetected,
  BannedRegionDetected,
  BlockedRegionDetected,
  VpnDetected,
  IpRateLimitExceeded, // User has attempted to many times from the same IP
  IpReputationSuspect, // User has an IP address with a bad reputation
  // Location-related errors end
  IdentityTokenIpMismatch, // User IP address does not match the IP address in the identity token
  IdentityTokenExpired, // Identity token from face-auth is expired
  IdentityTokenInvalid, // Identity token from face-auth is invalid
  PartnerRejected, // Partner rejected the pass
  ComplianceCheckFailed, // Compliance check failed
  RateLimited, // User has been rate limited
  Unknown, // Default value for unknown error
}

export const uniquenessErrors = [ErrorType.NotUnique, ErrorType.RevocationTooRecent];
export const suspiciousIpErrors = [ErrorType.IpRateLimitExceeded, ErrorType.IpReputationSuspect];
export const disallowedCountryErrors = [ErrorType.BannedCountryDetected, ErrorType.BlockedCountryDetected];
export const disallowedRegionErrors = [ErrorType.BannedRegionDetected, ErrorType.BlockedRegionDetected];
export const sessionExpiredErrors = [ErrorType.IdentityTokenExpired, ErrorType.IdentityTokenInvalid];
export const partnerRejectedErrors = [ErrorType.PartnerRejected];
export const complianceErrors = [ErrorType.ComplianceCheckFailed, ErrorType.RateLimited];

// this should match the error codes returned by the GK-API
export const StrategyErrorCode = {
  UNIQUENESS_FOUND_MATCHES_REJECTION: '0x4001',
  UNIQUENESS_REVOCATION_TOO_RECENT_REJECTION: '0x4002',
  IP_RATE_LIMIT_REJECTION: '0x4003',
  IP_REPUTATION_REJECTION: '0x4004',
  IDENTITY_TOKEN_IP_MISMATCH_ERROR: '0x4005',
  PARTNER_REJECTED_ERROR: '0x4006',
  IDENTITY_TOKEN_EXPIRED: '0x4007',
  IDENTITY_TOKEN_INVALID: '0x4008',
  COMPLIANCE_STORE_FAILED_CHECK: '0x4009',
  BLOCKED_COUNTRY_DETECTED: '0x4010',
  BANNED_COUNTRY_DETECTED: '0x4011',
  VPN_DETECTED: '0x4012',
  BLOCKED_REGION_DETECTED: '0x4013',
  BANNED_REGION_DETECTED: '0x4014',
};

// This maps the error codes returned by the GK-API to the error types to determine which error message to show.
const errorMapping: { [key: string]: ErrorType } = {
  [StrategyErrorCode.UNIQUENESS_FOUND_MATCHES_REJECTION]: ErrorType.NotUnique,
  [StrategyErrorCode.UNIQUENESS_REVOCATION_TOO_RECENT_REJECTION]: ErrorType.RevocationTooRecent,
  // Location errors start here
  [StrategyErrorCode.IP_RATE_LIMIT_REJECTION]: ErrorType.IpRateLimitExceeded,
  [StrategyErrorCode.IP_REPUTATION_REJECTION]: ErrorType.IpReputationSuspect,
  [StrategyErrorCode.BANNED_COUNTRY_DETECTED]: ErrorType.BannedCountryDetected,
  [StrategyErrorCode.BLOCKED_COUNTRY_DETECTED]: ErrorType.BlockedCountryDetected,
  [StrategyErrorCode.BANNED_REGION_DETECTED]: ErrorType.BannedRegionDetected,
  [StrategyErrorCode.BLOCKED_REGION_DETECTED]: ErrorType.BlockedRegionDetected,
  [StrategyErrorCode.VPN_DETECTED]: ErrorType.VpnDetected,
  // Location errors end here
  [StrategyErrorCode.IDENTITY_TOKEN_IP_MISMATCH_ERROR]: ErrorType.IdentityTokenIpMismatch,
  [StrategyErrorCode.PARTNER_REJECTED_ERROR]: ErrorType.PartnerRejected,
  [StrategyErrorCode.IDENTITY_TOKEN_EXPIRED]: ErrorType.IdentityTokenExpired,
  [StrategyErrorCode.IDENTITY_TOKEN_INVALID]: ErrorType.IdentityTokenInvalid,
  [StrategyErrorCode.COMPLIANCE_STORE_FAILED_CHECK]: ErrorType.ComplianceCheckFailed,
};

/**
 * Get the error type from the error code returned by the GK-API.
 * @param errorCode The error code returned by the GK-API.
 * @returns The error type.
 */
export function getErrorType(errorCode?: string): ErrorType {
  if (!errorCode) {
    return ErrorType.TokenRejected;
  }

  if (errorCode === 'compliance_store_check_failed') {
    return ErrorType.ComplianceCheckFailed;
  }

  if (errorCode === 'rate_limited') {
    return ErrorType.RateLimited;
  }

  return errorMapping[errorCode] || ErrorType.TokenRejected;
}

export type GatekeeperRejectionPayload = {
  matches?: string[];
  revocationTimestamp?: number;
};
